import { SubscriptionStateEnum } from "@/features/billing/types";
import { TOrganization } from "../../types";

const EXPIRED_STATUSES = new Set<string>([
  SubscriptionStateEnum.CANCELED,
  SubscriptionStateEnum.UNPAID,
  SubscriptionStateEnum.INCOMPLETE_EXPIRED,
  SubscriptionStateEnum.ACTIVE_CANCELED,
]);

export type TLicenseBadgeVariant =
  | "expired"
  | "not_configured"
  | "payment_failed"
  | "over_capacity"
  | "almost_full"
  | "active";

export type TOrganizationLicenseRowUi = {
  badgeVariant: TLicenseBadgeVariant;
  usedDevices: number;
  totalLicenses: number;
  progressValue: number;
  progressColor: "green" | "yellow" | "red";
  available: number | null;
  usageTooltip: string | undefined;
  showUsageRow: boolean;
  isNotConfigured: boolean;
};

function resolveBadgeVariant(
  isExpired: boolean,
  isNotConfigured: boolean,
  isPaymentPastDue: boolean,
  isOverCapacity: boolean,
  isAlmostFull: boolean,
): TLicenseBadgeVariant {
  if (isExpired) return "expired";
  if (isNotConfigured) return "not_configured";
  if (isPaymentPastDue) return "payment_failed";
  if (isOverCapacity) return "over_capacity";
  if (isAlmostFull) return "almost_full";
  return "active";
}

function resolveProgressColor(
  isOverCapacity: boolean,
  usageRatio: number,
): "green" | "yellow" | "red" {
  if (isOverCapacity || usageRatio > 0.95) return "red";
  if (usageRatio > 0.8) return "yellow";
  return "green";
}

const notConfiguredUi = (): TOrganizationLicenseRowUi => ({
  badgeVariant: "not_configured",
  usedDevices: 0,
  totalLicenses: 0,
  progressValue: 0,
  progressColor: "green",
  available: null,
  usageTooltip: undefined,
  showUsageRow: false,
  isNotConfigured: true,
});

export function getOrganizationLicenseRowUi(
  organization: TOrganization,
): TOrganizationLicenseRowUi {
  const billing = organization.billingDetails;
  if (!billing || typeof billing.status !== "string") {
    return notConfiguredUi();
  }

  const status = billing.status;
  const totalLicenses = Math.max(0, Number(billing.devicesCount) || 0);
  const osCounts = Array.isArray(billing.osCounts) ? billing.osCounts : [];
  const usedDevices = osCounts.reduce(
    (acc, curr) => acc + (Number(curr?.osCount) || 0),
    0,
  );

  const isExpired = EXPIRED_STATUSES.has(status);
  const hasSeatCapacity = totalLicenses > 0;
  const usageRatio = hasSeatCapacity ? usedDevices / totalLicenses : 0;

  const isNotConfigured =
    !isExpired &&
    status !== SubscriptionStateEnum.PAST_DUE &&
    (status === SubscriptionStateEnum.INCOMPLETE || !hasSeatCapacity);

  const isPaymentPastDue =
    !isExpired && !isNotConfigured && status === SubscriptionStateEnum.PAST_DUE;

  const isOverCapacity = hasSeatCapacity && usedDevices > totalLicenses;
  const isAlmostFull = hasSeatCapacity && !isOverCapacity && usageRatio > 0.8;

  const badgeVariant = resolveBadgeVariant(
    isExpired,
    isNotConfigured,
    isPaymentPastDue,
    isOverCapacity,
    isAlmostFull,
  );

  const progressValue = Math.min(100, hasSeatCapacity ? usageRatio * 100 : 0);
  const progressColor = resolveProgressColor(isOverCapacity, usageRatio);

  const showUsageRow = hasSeatCapacity && !isNotConfigured;
  const available = showUsageRow
    ? Math.max(0, totalLicenses - usedDevices)
    : null;
  const usageTooltip = showUsageRow
    ? `${usedDevices} devices are currently enrolled out of ${totalLicenses} available licenses.`
    : undefined;

  return {
    badgeVariant,
    usedDevices,
    totalLicenses,
    progressValue,
    progressColor,
    available,
    usageTooltip,
    showUsageRow,
    isNotConfigured,
  };
}
